import { useMemo, useState } from "react";
import { useT } from "@hana/i18n";
import "./bookingI18n";
import {
  IconCalendar,
  IconExternalLink,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSend,
  IconShip,
  IconSparkles,
} from "@tabler/icons-react";
import { Button } from "@hana/ui";
import type { BookingRequest } from "./types";
import {
  mark,
  vesselFilter,
  vesselMarks,
  vesselRows,
  type Mark,
} from "./vesselView";
import s from "./vesselView.module.css";

const TONE = { err: s.tErr, warn: s.tWarn, lb: s.tLb } as const;
const DOT = { err: s.dotErr, warn: s.dotWarn, lb: "" } as const;
const CHIP = { err: s.err, warn: s.warn, lb: "" } as const;

const chipText = (m: Mark) => `${m.k} ${m.v}${m.cd ? ` · ${m.cd}` : ""}`;

interface Props {
  requests: readonly BookingRequest[];
  /** mở hồ sơ tàu (VesselDrawer) */
  onProfile: (name: string) => void;
  /** mở 2 form của yêu cầu */
  onOpenReq: (r: BookingRequest) => void;
  /** quay về góc nhìn Yêu cầu, lọc theo tàu */
  onInRequests: () => void;
  onWrite: () => void;
}

/** Mint-Booking › góc nhìn Tàu (phím 2): danh sách chuyến bên trái, chi tiết chuyến + yêu cầu trên tàu bên phải. */
export function VesselView({
  requests,
  onProfile,
  onOpenReq,
  onInRequests,
  onWrite,
}: Props) {
  const t = useT();
  const [mode, setMode] = useState<"bk" | "all">("bk");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const all = useMemo(() => vesselRows(requests), [requests]);
  const list = useMemo(() => vesselFilter(all, mode, q), [all, mode, q]);
  const cur = list.find((v) => v.key === sel) ?? list[0] ?? null;
  const haveN = all.filter((v) => v.rows.length > 0).length;

  return (
    <>
      <div className={s.bar}>
        <div role="group" aria-label="Phạm vi tàu" className={s.modes}>
          {(
            [
              ["bk", "Có booking", haveN],
              ["all", "Tất cả", all.length],
            ] as const
          ).map(([k, label, n]) => (
            <button
              key={k}
              type="button"
              aria-pressed={mode === k}
              className={`${s.mode} ${mode === k ? s.on : ""}`}
              onClick={() => setMode(k)}
            >
              {label} <span>{n}</span>
            </button>
          ))}
        </div>
        <label className={s.search}>
          <IconSearch size={15} aria-hidden="true" />
          <input
            aria-label="Tìm tàu, chuyến, hãng, terminal"
            placeholder="Tàu / chuyến / hãng / terminal"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <Button
          variant="secondary"
          icon={<IconPlus size={16} aria-hidden="true" />}
          onClick={onWrite}
        >
          {t("bkx.newVoyage")}
        </Button>
      </div>
      <div className={s.grid}>
        <section aria-label="Danh sách tàu" className={s.list}>
          {list.map((v) => (
            <button
              key={v.key}
              type="button"
              className={s.item}
              aria-pressed={cur?.key === v.key}
              onClick={() => setSel(v.key)}
            >
              <span className={s.l1}>
                <b>{v.name}</b>
                <span className={s.meta}>{v.meta}</span>
                {v.listFlag && <span className={s.pill}>{t("bkx.resched")}</span>}
                <span className={s.n}>{v.rows.length} {t("bkx.requests")}</span>
              </span>
              <span className={s.l2}>
                {[mark("SI", v.si), mark("CY", v.cy)].map((m) => (
                  <span key={m.k} className={`${s.chip} ${CHIP[m.tone]}`}>
                    {chipText(m)}
                  </span>
                ))}
              </span>
            </button>
          ))}
          {list.length === 0 && <div className={s.none}>{t("bkx.noVessel")}</div>}
        </section>
        <aside aria-label="Chi tiết tàu" className={s.side}>
          <section className={s.card}>
            <div className={s.h}>
              <IconShip size={18} aria-hidden="true" />
              <h2>{cur?.name ?? "Chọn một tàu"}</h2>
              {cur && (
                <button
                  type="button"
                  className={s.link}
                  data-vlink={cur.name}
                  title="Hồ sơ tàu"
                  onClick={() => onProfile(cur.name)}
                >
                  <IconShip size={14} aria-hidden="true" />
                  {t("bkx.vesselProfile")}
                </button>
              )}
            </div>
            {cur && (
              <>
                <span className={s.meta}>{cur.meta}</span>
                {vesselMarks(cur).map((m) => (
                  <div key={m.k} className={s.mk}>
                    <span className={`${s.dot} ${DOT[m.tone]}`} />
                    <span>{m.k}</span>
                    <b>{m.v}</b>
                    <small className={TONE[m.tone]}>{m.cd}</small>
                  </div>
                ))}
                {cur.change && (
                  <div className={s.chg}>
                    <b>{t("bkx.cutoffDiff")}</b>
                    <span>{cur.change}</span>
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<IconCalendar size={16} aria-hidden="true" />}
                      onClick={onWrite}
                    >
                      {t("bkx.updSchedule")}
                    </Button>
                  </div>
                )}
                <div className={s.acts}>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<IconSend size={16} aria-hidden="true" />}
                    onClick={onWrite}
                  >
                    {t("bkx.sendNotice")}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<IconSparkles size={14} aria-hidden="true" />}
                    aria-label="Hana đọc thư lịch tàu · AI Input"
                    onClick={onWrite}
                  >
                    {t("bkx.aiInput")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<IconExternalLink size={16} aria-hidden="true" />}
                    onClick={onInRequests}
                  >
                    {t("bkx.openInReq")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<IconPencil size={16} aria-hidden="true" />}
                    onClick={onWrite}
                  >
                    {t("bkx.editSchedule")}
                  </Button>
                </div>
              </>
            )}
          </section>
          {cur && (
            <section className={s.cardReq}>
              <div className={`${s.h} ${s.hReq}`}>
                <h2>{t("bkx.reqOnVessel")}</h2>
                <span>{cur.rows.length} {t("bkx.requests")}</span>
              </div>
              {cur.rows.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={s.req}
                  onClick={() => onOpenReq(r)}
                >
                  <b>{r.jobId}</b>
                  <span className={s.cus}>{r.customer}</span>
                  <span className={s.av} title={r.owner ?? t("booking.claim")}>
                    {r.owner ? r.owner.slice(0, 2).toUpperCase() : "—"}
                  </span>
                  <small className={r.notified ? s.tOk : s.tWarn}>
                    {r.notified ? "đã báo khách" : "chưa báo"}
                  </small>
                </button>
              ))}
              {cur.rows.length === 0 && (
                <div className={s.empty}>{t("bkx.noReq")}</div>
              )}
            </section>
          )}
        </aside>
      </div>
    </>
  );
}
